import React, { useMemo, useEffect, useRef, useState } from "react";
import "./styles.css";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import { AgGridReact } from "ag-grid-react";
import { ColDef, IDatasource, IGetRowsParams, RowSelectionOptions, SelectionChangedEvent, themeQuartz } from "ag-grid-community";
import { AG_GRID_LOCALE_CZ } from '@ag-grid-community/locale';
import useAppData from "@/store/useAppData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import { faArrowLeft, faWarehouse } from "@fortawesome/free-solid-svg-icons";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { FilterKind, Item, ListScope, Stock, UiColumn } from "../ItemList/types";
import axios from "axios";
// import { DeicingItem } from "./types";
import Card from "../Card";
import useDeviceType from "@/Utils/useDeviceType";
import { DeicingItem } from "../DeicingList/types";
import { DeicingGridRowSummary } from "../ItemList/useItemListLogicOdmraz";
import { urlToDataUrl } from "@/Utils/exportInventoryResultToPdf";
import { exportDeicingProtocolPdf } from "@/Utils/exportDeiceToPdf";
import logoAirlines from "../Login/logoAirlines.png";

// const myTheme = themeQuartz.withParams({
//     fontSize: 17,
//     headerFontSize: 17,
//     rowHeight: 55,
//     headerHeight: 48,
// });

const desktopTheme = themeQuartz.withParams({
    fontSize: 17,
    headerFontSize: 17,
    rowHeight: 55,
    headerHeight: 48,
});

const tabletTheme = themeQuartz.withParams({
    fontSize: 15,
    headerFontSize: 15,
    rowHeight: 70,
    headerHeight: 46,
});

const mobileTheme = themeQuartz.withParams({
    fontSize: 14,
    headerFontSize: 14,
    rowHeight: 80,
    headerHeight: 48,
});

function mapFilter(filter?: FilterKind): ColDef["filter"] {
    if (filter === "text") return "agTextColumnFilter";
    if (filter === "number") return "agNumberColumnFilter";
    if (filter === "set") return "agSetColumnFilter";
    return false;
}


export interface DeicingListProps {
    onSettingsClick?: () => void;
    onProfileClick?: () => void;
    onChangedScope: (scope: ListScope) => void;
    scope: string;
}

const ItemListDeicing: React.FC<DeicingListProps> = ({ onSettingsClick, onProfileClick, onChangedScope, scope }: DeicingListProps) => {

    const loggedUser = useAppData(state => state.user);
    const setSelectedDeicing = useAppData(state => state.setSelectedDeicing);
    const selectedDeicing = useAppData(state => state.selectedDeicing);
    const selectedStock = useAppData(state => state.selectedStock);
    const setSelectedStock = useAppData(state => state.setSelectedStock);
    const selectedModule = useAppData(state => state.selectedModule);
    const deviceType = useDeviceType();
    const gridRef = useRef<AgGridReact<DeicingGridRowSummary>>(null);
    const [stocksState, setStocksState] = React.useState<Stock[]>([]);
    const [rowData, setRowData] = useState<any[]>([]);

    const router = useRouter(
        useShallow(state => ({
            go: state.go,
            history: state.history
        }))
    );

    const rowHeight = useMemo(() => {
        switch (deviceType) {
            case "mobile":
                return 80;

            case "tablet":
                return 70;

            default:
                return 55;
        }
    }, [deviceType]);

    const dateFormat = (date: string, dateOnly?: boolean) => {
        const d = new Date(date);

        const pad = (n: number) => String(n).padStart(2, "0");

        const formattedDate =
            pad(d.getUTCDate()) + "." +
            pad(d.getUTCMonth() + 1) + "." +
            d.getUTCFullYear();

        if (dateOnly) {
            return formattedDate;
        }

        return (
            formattedDate + " " +
            pad(d.getUTCHours()) + ":" +
            pad(d.getUTCMinutes()) + ":" +
            pad(d.getUTCSeconds())
        );
    };

    // useEffect(() => {
    //     gridRef.current?.api.resetRowHeights();
    // }, [deviceType]);

    useEffect(() => {
        loadStocks();
    }, [selectedModule?.id]);

    useEffect(() => {
        if (!gridRef) return;

        const interval = setInterval(() => {
            gridRef?.current?.api.refreshInfiniteCache();
        }, 20_000);

        return () => clearInterval(interval);
    }, [gridRef]);

    useEffect(() => {
        const load = async () => {
            try {

                const result = await getDeicings(
                    0,
                    1000000,
                    {}
                );

                let data = formatDeicingDataToSummaryData(result.rows || []);

                data = data.filter(item => item?.aircraftRegistration && item?.flightNumber);

                data.sort((a, b) => {
                    const checkedCompare = Number(a.checked ?? false) - Number(b.checked ?? false);

                    if (checkedCompare !== 0) return checkedCompare;

                    const startCompare = (b.type1Start ?? 0) - (a.type1Start ?? 0);

                    if (startCompare !== 0) return startCompare;

                    return (a.aircraftRegistration ?? "").localeCompare(
                        b.aircraftRegistration ?? ""
                    );
                });

                setRowData(data);

            } catch (error) {
                console.error(error);
                setRowData([]);
            }
        };

        load();
    }, []);

    const loadStocks = async () => {

        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/stock/getAll",
                    data: {
                        userId: loggedUser?.id,
                        module: selectedModule?.id
                    }
                })
            );

            if (!res?.data?.stocks?.length || res?.data?.stocks?.length === 0) {
                // setSelectedStockState(undefined);
                setSelectedStock(null);

                setStocksState([]);

                return;
            }

            setStocksState(res.data.stocks);

            if (selectedStock && res.data.stocks.find(stock => stock.id === selectedStock.id)) {
                // setSelectedStockState(selectedStock?.id);
            } else {
                // setSelectedStockState(String(res.data.stocks[0].id))
                setSelectedStock(res.data.stocks[0]);
            }

        } catch (err) {
            throw err;
        }
    }

    const gridTheme = useMemo(() => {
        switch (deviceType) {
            case "mobile":
                return mobileTheme;

            case "tablet":
                return tabletTheme;

            default:
                return desktopTheme;
        }
    }, [deviceType]);

    const columns = useMemo<UiColumn<DeicingGridRowSummary>[]>(() => [
        {
            field: "date",
            headerName: "Datum",
            flex: 0.8,
            minWidth: 80,
            filter: "text",
            floatingFilter: true,
            mobile: true,
            tablet: false,
            desktop: false,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value, true);
            }
        },
        {
            field: "type1Start",
            headerName: "Začátek",
            flex: 0.8,
            minWidth: 115,
            filter: "text",
            floatingFilter: true,
            tablet: true,
            desktop: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        },
        {
            field: "type1End",
            headerName: "Konec",
            flex: 0.8,
            minWidth: 115,
            filter: "text",
            floatingFilter: true,
            tablet: true,
            desktop: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        },
        // {
        //     field: "type1FluidConsumption",
        //     headerName: "Kapalina I",
        //     flex: 0.8,
        //     minWidth: 80,
        //     filter: "number",
        //     floatingFilter: true,
        //     desktop: true
        // },
        // {
        //     field: "fluidRatio",
        //     headerName: "Poměr I",
        //     flex: 0.8,
        //     minWidth: 80,
        //     filter: "number",
        //     floatingFilter: true,
        //     desktop: true
        // },
        // {
        //     field: "type2Start",
        //     headerName: "Začátek II",
        //     flex: 1,
        //     minWidth: 120,
        //     filter: "text",
        //     floatingFilter: true,
        //     tablet: true,
        //     desktop: true,
        //     valueFormatter: params => {
        //         if (!params.value) return "";

        //         return dateFormat(params.value);
        //     }
        // },
        // {
        //     field: "type2End",
        //     headerName: "Konec II",
        //     flex: 1,
        //     minWidth: 120,
        //     filter: "text",
        //     floatingFilter: true,
        //     tablet: true,
        //     desktop: true,
        //     valueFormatter: params => {
        //         if (!params.value) return "";

        //         return dateFormat(params.value);
        //     }
        // },
        // {
        //     field: "type2FluidConsumption",
        //     headerName: "Kapalina II",
        //     flex: 0.8,
        //     minWidth: 80,
        //     filter: "number",
        //     floatingFilter: true,
        //     desktop: true
        // },
        // {
        //     field: "waterConsumption",
        //     headerName: "Voda",
        //     flex: 0.8,
        //     minWidth: 80,
        //     filter: "number",
        //     floatingFilter: true,
        //     desktop: true
        // },
        // {
        //     field: "stockName",
        //     headerName: "Vozidlo",
        //     flex: 1,
        //     minWidth: 80,
        //     filter: "text",
        //     floatingFilter: true,
        //     mobile: true,
        //     tablet: true,
        //     desktop: true
        // },
        {
            field: "flightNumber",
            headerName: "Číslo letu",
            flex: 0.8,
            minWidth: 80,
            filter: "text",
            floatingFilter: true,
            mobile: true,
            tablet: true,
            desktop: true
        },
        {
            field: "aircraftRegistration",
            headerName: "Imatrikulace",
            flex: 1,
            minWidth: 80,
            filter: "text",
            floatingFilter: true,
            mobile: true,
            tablet: true,
            desktop: true
        },
        {
            field: "checked",
            headerName: "Kontrola",
            flex: 0.7,
            minWidth: 70,
            filter: "set",
            floatingFilter: true,
            mobile: true,
            tablet: true,
            desktop: true,
            valueFormatter: params => params.value ? "Ano" : "Ne"
        }
    ], []);

    // const columns: UiColumn<DeicingItem>[] = [
    //     // { field: "id", headerName: "Id", flex: 1, filter: "number", floatingFilter: true },
    //     { field: "type1Start", headerName: "Začátek odmrazování I", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type1End", headerName: "Konec odmrazování I", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type1FluidConsumption", headerName: "Spotřeba kapaliny I", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "fluidRatio", headerName: "Poměr kapaliny I", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type2Start", headerName: "Začátek odmrazování II", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type2End", headerName: "Konec odmrazování II", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type2FluidConsumption", headerName: "Spotřeba kapaliny II", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "waterConsumption", headerName: "Spotřeba vody", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "stand", headerName: "Stojánka", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "company", headerName: "Společnost", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "aircraftType", headerName: "Typ letadla", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "aircraftRegistration", headerName: "Imatrikulace letadla", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "flightNumber", headerName: "Číslo letu", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "oat", headerName: "OAT", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "wingTemperature", headerName: "Teplota křídel", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "weatherCode", headerName: "Počasí", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "supervisor", headerName: "Sluchátka", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "checked", headerName: "Zkontrolováno", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "checkedBy", headerName: "Zkontroloval", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "lastModified", headerName: "Upraveno", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "modifiedBy", headerName: "Upravil", flex: 3, filter: "text", floatingFilter: true },
    // ];

    // const formatDeicingDataToSummaryData = (
    //     rows: DeicingItem[]
    // ): DeicingGridRowSummary[] => {
    //     const result: DeicingGridRowSummary[] = [];

    //     const groupedByFlight = new Map<
    //         string,
    //         Map<string, DeicingItem[]>
    //     >();

    //     rows.forEach(item => {

    //         const flightNumber = item?.aircraftRegistration && item?.flightNumber ? `${item?.aircraftRegistration}-${item?.flightNumber}` : "---";

    //         if (!groupedByFlight.has(flightNumber)) {
    //             groupedByFlight.set(flightNumber, new Map());
    //         }

    //         const deicings = groupedByFlight.get(flightNumber)!;

    //         const deicingId = item?.id ?? "---";

    //         if (!deicings.has(deicingId)) {
    //             deicings.set(deicingId, []);
    //         }

    //         deicings.get(deicingId)!.push(item);
    //     });

    //     groupedByFlight.forEach(flight => {

    //         const resultItem: DeicingGridRowSummary = {};

    //         //const firstDeicingItem = flight.entries[0][0] as DeicingItem;
    //         const firstDeicingItem = flight.values().next().value?.[0];

    //         // resultItem.stand = firstDeicingItem?.stand;
    //         // resultItem.company = firstDeicingItem?.company;
    //         // resultItem.aircraftType = firstDeicingItem?.aircraftType;
    //         resultItem.aircraftRegistration = firstDeicingItem?.aircraftRegistration;
    //         resultItem.flightNumber = firstDeicingItem?.flightNumber;
    //         // resultItem.oat = firstDeicingItem?.oat;
    //         // resultItem.wingTemperature = firstDeicingItem?.wingTemperature;
    //         // resultItem.weatherName = firstDeicingItem?.weatherName;
    //         // resultItem.weatherCode = firstDeicingItem?.weatherCode;
    //         // resultItem.companyName = firstDeicingItem?.companyName;
    //         // resultItem.checkedBy = firstDeicingItem?.checkedByName;
    //         resultItem.date = firstDeicingItem?.date;
    //         // resultItem.most = flight.size > 1;
    //         // resultItem.checkedDate = firstDeicingItem?.checkedDate;
    //         resultItem.checked = firstDeicingItem?.checked;
    //         resultItem.id = firstDeicingItem?.id;

    //         // let index = 0;
    //         // let allNotes = "";
    //         // let type2FluidConsumption = 0;
    //         let firstStart: number | null = null;
    //         let lastEnd: number | null = null;
    //         // let lowestWingTemperature: number | null = null;
    //         // let lowestOat: number | null = null;
    //         flight.forEach((deice) => {

    //             // if (index === 0) {
    //             //     resultItem.deice1 = deice[0]?.stockName;
    //             //     resultItem.operator1 = deice[0]?.operator1;
    //             // }

    //             // if (index === 1) {
    //             //     resultItem.deice2 = deice[0]?.stockName;
    //             //     resultItem.operator2 = deice[0]?.operator1;
    //             // }

    //             // if (index === 2) {
    //             //     resultItem.deice3 = deice[0]?.stockName;
    //             //     resultItem.operator3 = deice[0]?.operator1;
    //             // }

    //             // index++;

    //             // allNotes += deice[0]?.note && deice[0]?.note !== "" ? `${deice[0]?.note}|` : "";

    //             // deice.forEach(deiceItem => {
    //             //     allNotes += deiceItem.itemNote && deiceItem?.itemNote !== "" ? `${deiceItem?.itemNote}|` : "";
    //             // })

    //             // type2FluidConsumption += Number(deice[0]?.type3FluidConsumption) || 0;

    //             // lowestOat = deice[0]?.oat && lowestOat ? Math.min(deice[0]?.oat, lowestOat) : deice[0]?.oat ?? lowestOat;
    //             // lowestWingTemperature = deice[0]?.wingTemperature && lowestWingTemperature ? Math.min(deice[0]?.wingTemperature, lowestWingTemperature) : deice[0]?.wingTemperature ?? lowestWingTemperature;

    //             const starts = [deice[0]?.type1Start, deice[0]?.type2Start]
    //                 .filter(Boolean)
    //                 .map(value => new Date(value!).getTime())
    //                 .filter(Number.isFinite);

    //             if (starts?.length) {
    //                 firstStart = firstStart ? Math.min(...starts, firstStart) : Math.min(...starts)
    //             }

    //             const ends = [deice[0]?.type1End, deice[0]?.type2End]
    //                 .filter(Boolean)
    //                 .map(value => new Date(value!).getTime())
    //                 .filter(Number.isFinite);

    //             if (ends?.length) {
    //                 lastEnd = lastEnd ? Math.max(...ends, lastEnd) : Math.max(...ends)
    //             }
    //         })

    //         resultItem.type1Start = firstStart;
    //         resultItem.type1End = lastEnd;
    //         // resultItem.deicingDuration = Math.round(((lastEnd ?? 0) - (firstStart ?? 0)) / 60_000);
    //         // resultItem.notes = allNotes;
    //         // resultItem.type2FluidConsumption = type2FluidConsumption > 0 ? type2FluidConsumption : null;
    //         // resultItem.fluid2Ratio = type2FluidConsumption > 0 ? 100 : null;
    //         // resultItem.wingTemperature = lowestWingTemperature ?? lowestOat;
    //         // resultItem.oat = lowestOat;

    //         // let type1FluidConsumption = 0;
    //         // let waterConsumption = 0;
    //         // let type1FluidConsumptionMax = 0;
    //         // let fluid1Ratio = 0;

    //         // flight.forEach((deice) => {
    //         //     deice?.forEach(deiceItem => {
    //         //         type1FluidConsumption += Number(deiceItem.itemFluidConsumption) || 0;
    //         //         waterConsumption += Number(deiceItem.itemWaterConsumption) || 0;

    //         //         if (deiceItem?.itemFluidConsumption && Number(deiceItem?.itemFluidConsumption) > type1FluidConsumptionMax) {
    //         //             type1FluidConsumptionMax = Number(deiceItem?.itemFluidConsumption);
    //         //             fluid1Ratio = deiceItem?.itemFluidRatio && deiceItem?.itemFluidRatio != "" ? Number(deiceItem?.itemFluidRatio?.split("/")[0]) : 0;
    //         //         }
    //         //     })
    //         // })

    //         // resultItem.type1FluidConsumption = type1FluidConsumption > 0 ? type1FluidConsumption : null;
    //         // resultItem.waterConsumption = waterConsumption > 0 ? waterConsumption : null;
    //         // resultItem.fluid1Ratio = fluid1Ratio > 0 ? fluid1Ratio : null;

    //         result.push(resultItem);
    //     });

    //     return result;
    // };

    const formatDeicingDataToSummaryData = (
        rows: DeicingItem[]
    ): DeicingGridRowSummary[] => {
        const result: DeicingGridRowSummary[] = [];

        const groupedByFlight = new Map<
            string,
            Map<string, DeicingItem[]>
        >();

        rows.forEach(item => {

            const flightNumber = item?.aircraftRegistration && item?.flightNumber ? `${item?.aircraftRegistration}-${item?.flightNumber}` : "---";

            if (!groupedByFlight.has(flightNumber)) {
                groupedByFlight.set(flightNumber, new Map());
            }

            const deicings = groupedByFlight.get(flightNumber)!;

            const deicingId = item?.id ?? "---";

            if (!deicings.has(deicingId)) {
                deicings.set(deicingId, []);
            }

            deicings.get(deicingId)!.push(item);
        });

        groupedByFlight.forEach(flight => {

            const resultItem: DeicingGridRowSummary = {};

            //const firstDeicingItem = flight.entries[0][0] as DeicingItem;
            const firstDeicingItem = flight.values().next().value?.[0];

            resultItem.stand = firstDeicingItem?.standName;
            resultItem.company = firstDeicingItem?.company;
            resultItem.aircraftType = firstDeicingItem?.aircraftType;
            resultItem.aircraftRegistration = firstDeicingItem?.aircraftRegistration;
            resultItem.flightNumber = firstDeicingItem?.flightNumber;
            // resultItem.oat = firstDeicingItem?.oat;
            // resultItem.wingTemperature = firstDeicingItem?.wingTemperature;
            resultItem.weatherName = firstDeicingItem?.weatherName;
            resultItem.weatherEngName = firstDeicingItem?.weatherEngName;
            resultItem.weatherCode = firstDeicingItem?.weatherCode;
            resultItem.companyName = firstDeicingItem?.companyName;
            resultItem.checkedBy = firstDeicingItem?.checkedByName;
            resultItem.date = firstDeicingItem?.date;
            resultItem.most = flight.size > 1;
            resultItem.checkedDate = firstDeicingItem?.checkedDate;
            resultItem.checked = firstDeicingItem?.checked;
            resultItem.id = firstDeicingItem?.id;

            let index = 0;
            let allNotes = "";
            let type2FluidConsumption = 0;
            let firstStart: number | null = null;
            let lastEnd: number | null = null;
            let lowestWingTemperature: number | null = null;
            let lowestOat: number | null = null;
            flight.forEach((deice) => {

                if (index === 0) {
                    resultItem.deice1 = deice[0]?.stockName;
                    resultItem.operator1 = deice[0]?.operator1;

                    if (deice[0]?.nextOperatorName) {
                        resultItem.nextOperator1 = deice[0]?.nextOperatorName;
                    }
                }

                if (index === 1) {
                    resultItem.deice2 = deice[0]?.stockName;
                    resultItem.operator2 = deice[0]?.operator1;

                    if (deice[0]?.nextOperatorName) {
                        resultItem.nextOperator2 = deice[0]?.nextOperatorName;
                    }
                }

                if (index === 2) {
                    resultItem.deice3 = deice[0]?.stockName;
                    resultItem.operator3 = deice[0]?.operator1;

                    if (deice[0]?.nextOperatorName) {
                        resultItem.nextOperator3 = deice[0]?.nextOperatorName;
                    }
                }

                index++;

                allNotes += deice[0]?.note && deice[0]?.note !== "" ? `${deice[0]?.note}|` : "";

                deice.forEach(deiceItem => {
                    allNotes += deiceItem.itemNote && deiceItem?.itemNote !== "" ? `${deiceItem?.itemNote}|` : "";
                })

                type2FluidConsumption += Number(deice[0]?.type3FluidConsumption) || 0;

                lowestOat = deice[0]?.oat && lowestOat ? Math.min(deice[0]?.oat, lowestOat) : deice[0]?.oat ?? lowestOat;
                lowestWingTemperature = deice[0]?.wingTemperature && lowestWingTemperature ? Math.min(deice[0]?.wingTemperature, lowestWingTemperature) : deice[0]?.wingTemperature ?? lowestWingTemperature;

                const starts = [deice[0]?.type1Start, deice[0]?.type2Start]
                    .filter(Boolean)
                    .map(value => new Date(value!).getTime())
                    .filter(Number.isFinite);

                if (starts?.length) {
                    firstStart = firstStart ? Math.min(...starts, firstStart) : Math.min(...starts)
                }

                const ends = [deice[0]?.type1End, deice[0]?.type2End]
                    .filter(Boolean)
                    .map(value => new Date(value!).getTime())
                    .filter(Number.isFinite);

                if (ends?.length) {
                    lastEnd = lastEnd ? Math.max(...ends, lastEnd) : Math.max(...ends)
                }
            })

            resultItem.type1Start = firstStart;
            resultItem.type1End = lastEnd;
            resultItem.deicingDuration = Math.round(((lastEnd ?? 0) - (firstStart ?? 0)) / 60_000);
            resultItem.notes = allNotes;
            resultItem.type2FluidConsumption = type2FluidConsumption > 0 ? type2FluidConsumption : null;
            resultItem.fluid2Ratio = type2FluidConsumption > 0 ? 100 : null;
            resultItem.wingTemperature = lowestWingTemperature ?? lowestOat;
            resultItem.oat = lowestOat;

            let type1FluidConsumption = 0;
            let waterConsumption = 0;
            let type1FluidConsumptionMax = 0;
            let fluid1Ratio = 0;

            flight.forEach((deice) => {
                deice?.forEach(deiceItem => {
                    type1FluidConsumption += Number(deiceItem.itemFluidConsumption) || 0;
                    waterConsumption += Number(deiceItem.itemWaterConsumption) || 0;

                    if (deiceItem?.itemFluidConsumption && Number(deiceItem?.itemFluidConsumption) > type1FluidConsumptionMax) {
                        type1FluidConsumptionMax = Number(deiceItem?.itemFluidConsumption);
                        fluid1Ratio = deiceItem?.itemFluidRatio && deiceItem?.itemFluidRatio != "" ? Number(deiceItem?.itemFluidRatio?.split("/")[0]) : 0;
                    }
                })
            })

            resultItem.type1FluidConsumption = type1FluidConsumption > 0 ? type1FluidConsumption : null;
            resultItem.waterConsumption = waterConsumption > 0 ? waterConsumption : null;
            resultItem.fluid1Ratio = fluid1Ratio > 0 ? fluid1Ratio : null;

            result.push(resultItem);
        });

        return result;
    };

    const actions: FloatingAction[] = [
        // ...(loggedUser?.userrole != "viewer" ? [{
        //       key: "3",
        //       label: "Technický odmraz",
        //       icon: <FontAwesomeIcon icon={faWarehouse} />,
        //       onClick: () => router.go({ key: RouterStoreItemKey.DEICINGCARDTECH })
        //     }] : []),
        ...(loggedUser?.userrole != "viewer" ? [{
            key: "1",
            label: "Kontrola",
            //icon: <FontAwesomeIcon icon={faWarehouse} />,
            onClick: () => {
                //setSelectedDeicing(null);
                router.go({ key: RouterStoreItemKey.DEICINGCHECKCARD });
            }
        }] : [])
        // {
        //     key: "2",
        //     label: "Zpět",
        //     icon: <FontAwesomeIcon icon={faArrowLeft} />,
        //     onClick: () => { router.go({ key: RouterStoreItemKey.LISTITEMS }); }
        // }
    ];

    const datasource = useMemo<IDatasource>(() => ({
        getRows: async (params: IGetRowsParams) => {
            try {

                const result = await getDeicings(
                    params.startRow,
                    params.endRow,
                    { ...params }
                );

                const data = formatDeicingDataToSummaryData(result.rows || []);


                params.successCallback(data, result.totalCount);
            } catch {
                params.failCallback();
            }
        },
    }), [selectedStock?.id]);

    const getDeicings = async (startRow, endRow, agParams) => {
        try {

            const sortModel = [
                { sort: 'asc', colId: 'checked' },
                { sort: 'desc', colId: 'type1Start' },
                { sort: 'asc', colId: 'aircraftRegistration' }
            ];

            console.log(agParams);
            const res = await (
                axios({
                    method: "post",
                    url: "api/deicing/getAll",
                    data: {
                        start: startRow,
                        end: endRow,
                        filterModel: agParams.filterModel,
                        sortModel: sortModel,
                        withFluids: true
                        //stockId: selectedStock?.id
                    }
                })
            );

            return {
                rows: res.data.deicingItems,
                totalCount: res.data.totalCount
            };
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    // const handleSelectionChanged = (e) => {
    //     if (e.api.getSelectedRows()?.length > 0) {
    //         const a = e.api.getSelectedRows();
    //         setSelectedDeicing(e.api.getSelectedRows()[0]?.id);
    //     }
    // }

    const handleSelectionChanged = (
        event: SelectionChangedEvent<DeicingGridRowSummary>
    ) => {
        const [selectedRow] = event.api.getSelectedRows();

        setSelectedDeicing(selectedRow?.id ?? null);
    };

    // const columnDefs = useMemo<ColDef<Item>[]>(() => {

    //     return columns.map((c) => ({
    //         field: c.field as any,
    //         headerName: c.headerName,
    //         flex: c.flex,
    //         filter: mapFilter(c.filter),
    //         floatingFilter: c.floatingFilter,
    //         valueFormatter: c?.valueFormatter,
    //         checkboxSelection: c?.checkboxSelection,
    //         headerCheckboxSelection: c?.headerCheckboxSelection,
    //         maxWidth: c?.maxWidth,
    //         editable: c?.editable
    //     }));
    // }, [columns]);

    const columnDefs = useMemo<ColDef<DeicingGridRowSummary>[]>(() => {
        return columns
            .filter(column => column[deviceType] === true)
            .map(column => ({
                field: column.field as keyof DeicingGridRowSummary,
                headerName: column.headerName,
                flex: /*deviceType === "mobile" ? undefined :*/ column.flex,
                //width: deviceType === "mobile" ? column.minWidth : undefined,
                minWidth: column.minWidth,
                filter: mapFilter(column.filter),

                // Floating filters take too much vertical space on mobile.
                floatingFilter:
                    deviceType !== "mobile" &&
                    Boolean(column.floatingFilter),

                //wrapText: deviceType === "mobile",
                //autoHeight: true, //deviceType === "mobile",

                valueFormatter: column.valueFormatter,
                checkboxSelection: column.checkboxSelection,
                headerCheckboxSelection: column.headerCheckboxSelection,
                maxWidth: column.maxWidth,
                editable: column.editable,

                sortable: false,
                resizable: deviceType !== "mobile",

                // Shows complete column name on narrow displays.
                headerTooltip: column.headerName
            }));
    }, [columns, deviceType]);

    // const rowSelection: RowSelectionOptions<Item> = {
    //     mode: "singleRow",
    //     checkboxes: false,
    //     enableClickSelection: true,
    // };

    const rowSelection = useMemo<RowSelectionOptions<DeicingGridRowSummary>>(() => ({
        mode: "singleRow",
        checkboxes: false,
        enableClickSelection: true
    }), []);

    const defaultColDef = useMemo<ColDef<DeicingGridRowSummary>>(() => ({
        sortable: false,
        filter: true,
        suppressHeaderMenuButton: deviceType === "mobile"
    }), [deviceType]);

    const onChangeSelectedStock = (e) => {
        const stock = stocksState.find(
            stock => String(stock.id) === e.target.value
        );

        setSelectedStock(stock!);
        // setSelectedStock(stocksState.filter(stock => stock.id == e.target.value)[0]);
        // setSelectedStockState(e.target.value);

        // gridApiRef?.current?.deselectAll();
        // gridApiRef?.current?.refreshInfiniteCache();
    }

    const onPrintProtocol = async () => {

        const rows = gridRef.current?.api.getSelectedRows() ?? [];

        if (rows.length > 0) {

            const selectedItem: DeicingGridRowSummary = rows[0];

            const now = new Date();

            const dateFileName =
                now.getFullYear() +
                String(now.getMonth() + 1).padStart(2, "0") +
                String(now.getDate()).padStart(2, "0");

            const logoBase64 = await urlToDataUrl(logoAirlines);
            exportDeicingProtocolPdf(selectedItem, {
                fileName: `${dateFileName}-${selectedItem.aircraftRegistration}.pdf`,
                logoDataUrl: logoBase64
            });
        }
    }

    return <div className="deicing deicing-list"><Card
        title="Odmrazy (Seskupeno)"
        displaySelectedStock={false}
        displayLogo={false}
        //onSettingsClick={onSettingsClick}
        displayProfile={true}
    >
        <div className="item-list-card">

            <div className="item-list-toolbar">

                {/* <div className="item-list-toolbar"> */}

                    {loggedUser?.userrole === "admin" && <button
                        type="button"
                        className={`item-list-tab ${scope === "deicingOperator" ? "is-active" : ""}`}
                        onClick={() => { onChangedScope("deicingOperator"); }}
                    >
                        Operátor
                    </button>}
                    {loggedUser?.userrole === "admin" && <button
                        type="button"
                        className={`item-list-tab ${scope === "deicingCheck" ? "is-active" : ""}`}
                        onClick={() => { onChangedScope("deicingCheck"); }}
                    >
                        Kontrola
                    </button>}

                    <div className="item-list-toolbar-right">
                        <button
                            type="button"
                            className={`item-list-tab is-active`}
                            onClick={() => onPrintProtocol()}
                        >
                            Protokol (PDF)
                        </button>
                    </div>
                {/* </div> */}

                {/* <div className="item-list-toolbar-right">
                    <select
                        value={selectedStock?.id}
                        onChange={onChangeSelectedStock}
                    >
                        {stocksState.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.name}
                            </option>
                        ))}
                    </select>
                </div> */}
            </div>

            <div className="item-list-grid-wrapper">
                {/* <AgGridReact
                    theme={myTheme}
                    columnDefs={columnDefs}
                    rowModelType="infinite"
                    cacheBlockSize={100}
                    datasource={datasource}
                    localeText={AG_GRID_LOCALE_CZ}
                    // suppressRowClickSelection={true}
                    getRowId={(params) => params.data.id}
                    onSelectionChanged={handleSelectionChanged}
                    rowSelection={rowSelection}
                /> */}
                <AgGridReact<DeicingGridRowSummary>
                    ref={gridRef}
                    theme={gridTheme}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}

                    // rowModelType="infinite"
                    // cacheBlockSize={/*deviceType === "mobile" ? 50 : 100*/ 100}
                    // datasource={datasource}
                    rowData={rowData}
                    rowHeight={rowHeight}

                    localeText={AG_GRID_LOCALE_CZ}
                    getRowId={params => String(params.data.id)}

                    rowSelection={rowSelection}
                    onSelectionChanged={handleSelectionChanged}

                    animateRows={/*deviceType !== "mobile"*/ true}
                    suppressCellFocus={/*deviceType === "mobile"*/ true}
                    suppressMovableColumns={/*deviceType !== "desktop"*/ true}

                    tooltipShowDelay={300}
                    tooltipHideDelay={3000}
                />
            </div>

            {
                actions?.length ? (
                    <div className="item-list-actions">
                        <FloatingActions actions={actions} />
                    </div>
                ) : null
            }
        </div >
    </Card></div>;
};

export default ItemListDeicing;
